import localforage from 'localforage';
import { posApi } from './pos.api';
import type { CreatePosTransactionPayload } from './pos.api';

// Dedicated localforage instance for POS offline queue
const offlineStore = localforage.createInstance({
  name: 'erp_pos',
  storeName: 'offline_queue',
});

export interface OfflineTransaction {
  id: string;
  payload: CreatePosTransactionPayload;
  created_at: string;
  status: 'pending' | 'syncing' | 'failed';
  error?: string;
  retries: number;
}

const QUEUE_KEY = 'pos_offline_transactions';

/** Read the full queue from IndexedDB */
async function getQueue(): Promise<OfflineTransaction[]> {
  return (await offlineStore.getItem<OfflineTransaction[]>(QUEUE_KEY)) || [];
}

/** Persist the queue back to IndexedDB */
async function saveQueue(queue: OfflineTransaction[]): Promise<void> {
  await offlineStore.setItem(QUEUE_KEY, queue);
}

/** Add a transaction to the offline queue */
export async function enqueueTransaction(
  payload: CreatePosTransactionPayload,
): Promise<OfflineTransaction> {
  const tx: OfflineTransaction = {
    id: `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    payload,
    created_at: new Date().toISOString(),
    status: 'pending',
    retries: 0,
  };
  const queue = await getQueue();
  queue.push(tx);
  await saveQueue(queue);
  return tx;
}

/** Get the current number of pending transactions */
export async function getPendingCount(): Promise<number> {
  const queue = await getQueue();
  return queue.filter((t) => t.status === 'pending' || t.status === 'failed').length;
}

/** Get all transactions in the queue (for UI display) */
export async function getAllTransactions(): Promise<OfflineTransaction[]> {
  return getQueue();
}

/** Try to sync all pending transactions with the server. Returns count of synced items. */
export async function syncQueue(): Promise<{ synced: number; failed: number }> {
  const queue = await getQueue();
  let synced = 0;
  let failed = 0;

  for (const tx of queue) {
    if (tx.status !== 'pending' && tx.status !== 'failed') continue;

    tx.status = 'syncing';
    tx.retries += 1;

    try {
      await posApi.checkout(tx.payload);
      tx.status = 'pending'; // mark for removal
      synced++;
    } catch (err: unknown) {
      tx.status = 'failed';
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      tx.error = error.response?.data?.message || error.message || 'Неизвестная ошибка';
      failed++;
    }
  }

  // Remove successfully synced transactions from the queue
  const remaining = queue.filter((t) => t.retries === 0 || t.status === 'failed');
  await saveQueue(remaining);

  return { synced, failed };
}

/** Remove a specific transaction from the queue (e.g. user manually discards it) */
export async function removeTransaction(id: string): Promise<void> {
  const queue = await getQueue();
  await saveQueue(queue.filter((t) => t.id !== id));
}

/** Clear the entire offline queue */
export async function clearQueue(): Promise<void> {
  await saveQueue([]);
}

// ---------- Auto-Sync on Network Restore ----------

let listenerRegistered = false;

/** Register the online listener once. Safe to call multiple times. */
export function registerOnlineSync(
  onSyncResult?: (result: { synced: number; failed: number }) => void,
): void {
  if (listenerRegistered) return;
  listenerRegistered = true;

  window.addEventListener('online', async () => {
    const count = await getPendingCount();
    if (count === 0) return;

    const result = await syncQueue();
    onSyncResult?.(result);
  });
}
